import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Dumbbell, Bell, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUnreadCount, subscribeToNewNotifications } from '../lib/memberNotificationService';
import { TrainerMemberService } from '../lib/trainerMemberService';

const MEMBER_BOTTOM_NAV_ROUTES = [
  '/member-dashboard',
  '/active-workout-plans',
  '/notifications',
];

function isMemberBottomNavRoute(pathname: string): boolean {
  if (MEMBER_BOTTOM_NAV_ROUTES.some((r) => pathname === r)) return true;
  // Account hub only — settings detail pages (e.g. Help & Feedback) hide the tab bar.
  if (pathname === '/account') return true;
  return false;
}

const navItems = [
  { to: '/member-dashboard', label: 'Home', Icon: Home },
  { to: '/active-workout-plans', label: 'Workouts', Icon: Dumbbell },
  { to: '/notifications', label: 'Notifications', Icon: Bell },
  { to: '/account', label: 'Account', Icon: User },
] as const;

export function BottomNav() {
  const { user, profile } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const memberId = profile?.id || user?.id;
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);

  const refetchUnreadCount = useCallback(() => {
    if (!memberId) return;
    getUnreadCount(memberId).then(setUnreadNotificationCount).catch(() => setUnreadNotificationCount(0));
  }, [memberId]);

  const refetchPendingInvites = useCallback(() => {
    if (!memberId) return;
    TrainerMemberService.getPendingRequests(memberId).then((list) => setPendingInvitesCount(list.length)).catch(() => setPendingInvitesCount(0));
  }, [memberId]);

  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    refetchUnreadCount();
    refetchPendingInvites();
  }, [memberId, profile?.role, refetchUnreadCount, refetchPendingInvites]);

  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    const sub = subscribeToNewNotifications(memberId, refetchUnreadCount);
    return () => sub.unsubscribe();
  }, [memberId, profile?.role, refetchUnreadCount]);

  useEffect(() => {
    const onMarkedRead = () => refetchUnreadCount();
    window.addEventListener('member-notifications-marked-read', onMarkedRead);
    return () => window.removeEventListener('member-notifications-marked-read', onMarkedRead);
  }, [refetchUnreadCount]);

  // When dashboard Realtime detects a workout add/update/delete, refresh bell badge (trigger creates notification; this keeps badge in sync even if member_notifications Realtime is delayed or off)
  useEffect(() => {
    const onCountChanged = () => refetchUnreadCount();
    window.addEventListener('member-notification-count-changed', onCountChanged);
    return () => window.removeEventListener('member-notification-count-changed', onCountChanged);
  }, [refetchUnreadCount]);

  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => {
    const handler = (e: Event) => setDashboardLoading((e as CustomEvent).detail);
    window.addEventListener('dashboard-loading', handler);
    return () => window.removeEventListener('dashboard-loading', handler);
  }, []);

  if (!user || profile?.role !== 'member') return null;
  if (!isMemberBottomNavRoute(pathname)) return null;
  if (pathname === '/member-dashboard' && dashboardLoading) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-[60]"
      style={{ height: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
      aria-label="Bottom navigation"
    >
      {/* Transparent strip – no background */}
      <div className="absolute inset-0" aria-hidden />
      <nav
        className="fixed left-0 right-0 z-[61] flex justify-center px-4"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom) + 1.25rem)',
        }}
      >
      <div
        className="flex items-center justify-around w-full max-w-md rounded-full py-1 px-2"
        style={{
          minHeight: '2.25rem',
          background: 'rgba(255, 255, 255, 0.75)',
          WebkitBackdropFilter: 'blur(12px) saturate(150%)',
          backdropFilter: 'blur(12px) saturate(150%)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
        }}
      >
        {navItems.map(({ to, label, Icon }) => {
          const isActive = to === '/account' ? pathname.startsWith('/account') : pathname === to;
          const notificationTotal = unreadNotificationCount + pendingInvitesCount;
          const showNotificationBadge = to === '/notifications' && notificationTotal > 0;
          return (
            <NavLink
              key={to}
              to={to}
              className={`relative flex flex-col items-center justify-center gap-0.5 w-1/4 min-w-0 flex-shrink-0 px-3 py-1.5 rounded-full transition-colors ${
                isActive
                  ? 'bg-gray-400/35 dark:bg-gray-500/30 text-theme-bg-dark dark:text-white shadow-sm'
                  : 'text-theme-text/70 dark:text-white/70 hover:text-theme-text dark:hover:text-white hover:bg-white/15 dark:hover:bg-white/10'
              }`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="relative inline-flex">
                <Icon className="w-[1.4rem] h-[1.4rem] flex-shrink-0" strokeWidth={isActive ? 2.25 : 2} />
                {showNotificationBadge && (
                  <span className="absolute -top-1 -right-2 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full text-white text-[0.625rem] font-semibold leading-none" style={{ backgroundColor: '#c53030' }}>
                    {notificationTotal > 99 ? '99+' : notificationTotal}
                  </span>
                )}
              </span>
              <span className="text-[0.65rem] font-medium leading-tight">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
    </div>
  );
}
